import {
  IDoctor,
  IHospital,
  IInstitution,
  InstitutionType,
  INursingHome,
  INursingService,
  IPatient,
  IPharmacy,
  ISubunit,
} from '@alberta/konexi-shared';
import { AuthorizedKiwiInstitution } from '@alberta/konexi-shared/dist/institution/authorized-kiwi-institution';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  DoctorDB,
  HospitalDB,
  NursingHomeDB,
  NursingServiceDB,
  PharmacyDB,
  SubunitDB,
} from 'src/app/common/repository/databases';
import { FaxContact } from 'src/app/shared/models/fax-contact.model';
import { WizardStepItem } from 'src/app/shared/models/wizard-step-item';
import { QueryService } from 'src/app/shared/services/query/query.service';

export type InstitutionInfo = {
  name: string;
  id: string;
  parentInstitutionId: string;
  type: InstitutionType;
};

@Injectable({
  providedIn: 'root',
})
export class InstitutionsService {
  constructor(
    private readonly _queryService: QueryService,
    private readonly _router: Router,
    private readonly _translate: TranslateService
  ) {}

  /**
   * Construct a title for the given institution and institutionType
   */
  public async getInstitutionTitle(institutionId: string, institutionType: InstitutionType): Promise<string> {
    if (!institutionId || !institutionType) {
      return Promise.resolve('');
    }

    switch (institutionType) {
      case InstitutionType.Doctor:
        return this._queryService.get<IDoctor>(institutionId, DoctorDB).then(doctor => {
          return `${doctor.titleShort || ''} ${doctor.firstName || ''} ${doctor.lastName || ''}`;
        });
      case InstitutionType.Hospital:
      case InstitutionType.Pharmacy:
      case InstitutionType.NursingHome:
      case InstitutionType.NursingService:
        return this.getInstitution(institutionId, institutionType).then(institution => {
          return Promise.resolve(`${institution.name || ''}`);
        });
      default:
        return Promise.resolve('');
    }
  }

  /**
   * Returns an IInstitution promise of an institution (note: NOT doctor, this is a special type of institution)
   * @param id institution id
   * @param type institution type
   */
  public async getInstitution(id: string, type: InstitutionType): Promise<IInstitution> {
    switch (type) {
      case InstitutionType.Hospital:
        return await this._queryService.get<IHospital>(id, HospitalDB);
      case InstitutionType.Pharmacy:
        return await this._queryService.get<IPharmacy>(id, PharmacyDB);
      case InstitutionType.NursingHome:
        return await this._queryService.get<INursingHome>(id, NursingHomeDB);
      case InstitutionType.NursingService:
        return await this._queryService.get<INursingService>(id, NursingServiceDB);
      case InstitutionType.Doctor:
        throw new Error('InstitutionType Doctor cannot return an IInstitution. Use getDoctor() instead.');
      default:
        return undefined;
    }
  }

  public async getInstitutionByStringType(institutionId: string, type: string) {
    switch (type.toLowerCase()) {
      case 'hospital':
        return await this._queryService.get<IHospital>(institutionId, HospitalDB);
      case 'pharmacy':
        return await this._queryService.get<IPharmacy>(institutionId, PharmacyDB);
      case 'nursinghome':
        return await this._queryService.get<INursingHome>(institutionId, NursingHomeDB);
      case 'nursingservice':
        return await this._queryService.get<INursingService>(institutionId, NursingServiceDB);
      case 'doctor':
        throw new Error('InstitutionType Doctor cannot return an IInstitution. Use getDoctor() instead.');
      default:
        return undefined;
    }
  }

  public async getInstitutionAndSubunitNamesByPatient(patient: IPatient): Promise<InstitutionInfo[]> {
    // Doctor is retrieved separately because they do not use IInstitution
    const targetInstitutions: { id: string; type: InstitutionType }[] = [
      { id: patient.hospital?.hospitalId, type: InstitutionType.Hospital },
      { id: patient.pharmacyId, type: InstitutionType.Pharmacy },
      { id: patient.nursingHomeId, type: InstitutionType.NursingHome },
      { id: patient.nursingServiceId, type: InstitutionType.NursingService },
    ].filter(id => id.id !== undefined) as { id: string; type: InstitutionType }[];

    const institutionInformations: InstitutionInfo[] = [];

    for (const targetInstitution of targetInstitutions) {
      const institution = await this.getInstitutionByStringType(
        targetInstitution.id,
        InstitutionType[targetInstitution.type]
      );
      if (institution) {
        institutionInformations.push({
          name: institution.name,
          id: institution._id,
          parentInstitutionId: undefined,
          type: targetInstitution.type,
        });
      }
    }
    const subUnitIds: string[] = [
      patient.hospital?.subunitId,
      patient.pharmacy?.subunitId,
      patient.nursingHome?.subunitId,
      patient.nursingService?.subunitId,
    ].filter(id => id !== undefined) as string[];

    for (const subUnitId of subUnitIds) {
      const subUnit = await this.getSubunit(subUnitId);
      if (subUnit) {
        institutionInformations.push({
          name: subUnit.name,
          id: subUnit._id,
          parentInstitutionId: subUnit.institutionId,
          type: subUnit.institutionType,
        });
      }
    }

    const primaryDoctor = await this.getDoctor(patient.primaryDoctorId);
    if (primaryDoctor) {
      const title = `${primaryDoctor.title ? primaryDoctor.title : primaryDoctor.titleShort ? primaryDoctor.titleShort : ''}`;

      institutionInformations.push({
        name: `${title} ${primaryDoctor.firstName} ${primaryDoctor.lastName}`,
        id: primaryDoctor._id,
        parentInstitutionId: undefined,
        type: InstitutionType.Doctor,
      });
    }

    return institutionInformations;
  }

  public async getAuthorizedKiwiInstitutionNames(
    authorizedKiwiInstitutions: AuthorizedKiwiInstitution[]
  ): Promise<string[]> {
    if (!authorizedKiwiInstitutions) {
      return [];
    }

    const names: string[] = [];

    for (const kiwiInstitutionInformation of authorizedKiwiInstitutions) {
      if (kiwiInstitutionInformation.institutionType === InstitutionType.Doctor) {
        const doctor: IDoctor = await this.getDoctor(kiwiInstitutionInformation.id);

        if (!doctor) {
          continue;
        }
        const title = `${doctor.title ? doctor.title : doctor.titleShort ? doctor.titleShort : ''}`;
        const institutionTypeName = this.getInstitutionTypeName(InstitutionType.Doctor);

        names.push(`${institutionTypeName}: ${title} ${doctor.firstName} ${doctor.lastName}`);
      } else if (kiwiInstitutionInformation.parentInstitutionId) {
        // is subunit
        const subUnit: ISubunit = await this.getSubunit(kiwiInstitutionInformation.id);
        if (!subUnit) {
          continue;
        }
        const parentInstitution = subUnit.institutionType
          ? await this.getInstitutionByStringType(subUnit.institutionId, InstitutionType[subUnit.institutionType])
          : undefined;

        names.push(
          `${this._translate.instant('institutions.crm.subunit')}: ${parentInstitution?.name || ''} ${subUnit.name}`
        );
      } else {
        const institution: IHospital | IPharmacy | INursingHome | INursingService =
          await this.getInstitutionByStringType(
            kiwiInstitutionInformation.id,
            InstitutionType[kiwiInstitutionInformation.institutionType]
          );

        if (!institution) {
          continue;
        }
        const institutionTypeName = this.getInstitutionTypeName(kiwiInstitutionInformation.institutionType);

        names.push(`${institutionTypeName}: ${institution.name}`);
      }
    }
    return names;
  }

  /**
   * Return an IDoctor from the database. Use this instead of getInstitution() for doctors.
   * @param id doctor id
   */
  public async getDoctor(id: string): Promise<IDoctor> {
    return await this._queryService.get<IDoctor>(id, DoctorDB);
  }

  /**
   * Returns an ISubunit from the database.
   * @param id subunit id
   */
  public async getSubunit(id: string): Promise<ISubunit> {
    return await this._queryService.get<ISubunit>(id, SubunitDB);
  }

  /**
   * Construct the WizardStepItems for the given institution and institutiontype
   */
  public getTabItems(institutionType: InstitutionType, hasActivitesRight: number): WizardStepItem[] {
    const tabitems: WizardStepItem[] = [];
    tabitems.push(this.getInstitutionMasterTabItem(institutionType));
    if (institutionType !== InstitutionType.Doctor) {
      tabitems.push(new WizardStepItem('institutions.crm.subunits', 'subunits', 'alberta-subunit'));
    }
    tabitems.push(new WizardStepItem('institutions.crm.institutionContacts', 'contacts', 'alberta-contact-data'));
    tabitems.push(new WizardStepItem('institutions.crm.institutionNotes', 'notes', 'alberta-note'));
    if (
      (institutionType === InstitutionType.NursingHome || institutionType === InstitutionType.Hospital) &&
      hasActivitesRight
    ) {
      tabitems.push(new WizardStepItem('institutions.crm.activities', 'activities', 'alberta-activities'));
    }
    tabitems.push(new WizardStepItem('institutions.crm.documents', 'documents', 'alberta-document'));
    return tabitems;
  }

  private getInstitutionMasterTabItem(institutionType: InstitutionType): WizardStepItem {
    switch (institutionType) {
      case InstitutionType.Doctor:
        return new WizardStepItem('patient.details.sections.masterdata', 'doctor-master', 'alberta-clipboard');
      case InstitutionType.Hospital:
        return new WizardStepItem('patient.details.sections.masterdata', 'hospital-master', 'alberta-clipboard');
      case InstitutionType.Pharmacy:
        return new WizardStepItem('patient.details.sections.masterdata', 'pharmacy-master', 'alberta-clipboard');
      case InstitutionType.NursingHome:
        return new WizardStepItem('patient.details.sections.masterdata', 'nursing-home-master', 'alberta-clipboard');
      case InstitutionType.NursingService:
        return new WizardStepItem('patient.details.sections.masterdata', 'nursing-service-master', 'alberta-clipboard');
    }
  }

  public async getDoctorFaxContacts(id: string): Promise<FaxContact[]> {
    const doctor: IDoctor = await this._queryService.get<IDoctor>(id, DoctorDB);
    const noFax = !doctor.fax && !doctor.draft?.fax;
    if (!doctor || noFax || doctor.archived) {
      return [];
    }
    const title = `${doctor.title ? doctor.title : (doctor.titleShort ?? '')}`;
    const faxContacts: FaxContact[] = [];
    if (doctor.fax) {
      faxContacts.push(
        new FaxContact(
          `${title} ${doctor.firstName} ${doctor.lastName}`.trim(),
          doctor.fax,
          await this.getPostalAddress(doctor),
          'Arzt'
        )
      );
    }
    if (doctor.draft?.fax) {
      faxContacts.push(
        new FaxContact(
          `${title} ${doctor.firstName} ${doctor.lastName}`.trim(),
          doctor.draft.fax,
          await this.getPostalAddress(doctor),
          'Nicht verifiziert: Arzt'
        )
      );
    }
    return faxContacts;
  }

  public async getInstitutionFaxContact(institution: IInstitution): Promise<FaxContact[]> {
    if (institution?.fax && !institution.archived) {
      return [
        new FaxContact(institution.name, institution.fax, await this.getPostalAddress(institution), 'Institution'),
      ];
    } else {
      return [];
    }
  }

  public getInstitutionType(): string {
    const url = this._router.url;
    let type = url.slice(url.indexOf('//') + 2);
    type = type.slice(0, type.indexOf('/'));
    const hyphen = type.indexOf('-');
    type = type.charAt(0).toUpperCase() + type.slice(1);
    if (hyphen > 0) {
      type = type.slice(0, hyphen) + type.charAt(hyphen + 1).toUpperCase() + type.slice(hyphen + 2);
    }
    return type;
  }
  public getInstitutionTypeName(institutionType: InstitutionType): string {
    switch (institutionType) {
      case InstitutionType.Payer:
        return this._translate.instant('institutions.types.payer');
      case InstitutionType.Doctor:
        return this._translate.instant('institutions.types.doctor');
      case InstitutionType.Hospital:
        return this._translate.instant('institutions.types.hospital');
      case InstitutionType.Pharmacy:
        return this._translate.instant('institutions.types.pharmacy');
      case InstitutionType.NursingHome:
        return this._translate.instant('institutions.types.nursingHome');
      case InstitutionType.NursingService:
        return this._translate.instant('institutions.types.nursingService');
      case InstitutionType.JointPractice:
        return this._translate.instant('institutions.types.jointPractice');
      case InstitutionType.Unknown:
        return this._translate.instant('institutions.types.unknown');
      default:
        return '';
    }
  }

  private async getPostalAddress(institution: IInstitution | IDoctor) {
    const postalAddress = `${institution.address}, ${institution.postalCode} ${institution.city}`;
    return postalAddress
      .replace(/undefined/g, '')
      .replace(/\s\s+/g, ' ')
      .trim();
  }
}
